import React, { FC, useEffect, useState } from 'react';
import filter from 'lodash/filter';
import get from 'lodash/get';
import some from 'lodash/some';
import styled from 'styled-components';

import ActiveThreadsPage from './ActiveThreadsPage';
import { Edit } from 'src/presentation/components/pages/shared/rowActions/Edit';
import { EmptyStateWrapper } from 'src/presentation/components/pages/shared/TableList';
import ExternalLink from 'src/presentation/components/pages/shared/ExternalLink';
import { H3 } from '@ad-scripts/presentation/components/common/headers';
import useResource, { isLoadingStatus, STATUS_SUCCEEDED } from 'src/presentation/hooks/useResource';
import Mailbox from 'src/images/Mailbox.svg';
import NewPage from 'src/images/NewPage.svg';
import RouterLinkButton from 'src/presentation/components/RouterLinkButton';
import { Spinner } from 'src/presentation/components/pages/shared/Spinner';
import TablePage, { TablePageRender } from 'src/presentation/components/pages/shared/TablePage';
import { Tooltip } from 'src/presentation/components/agent_desktop_components';
import ThreadsEditor from './ThreadsEditor';
import useThreads from 'src/presentation/components/pages/threads/hooks/useThreads';
import WarningFill from '@ad-scripts/presentation/components/common/icons/fill/warning-fill';
import integrationsResource from 'src/actions/integrations/integrationsResource';
import isThreadDependencySatisfied from './helpers/isThreadDependencySatisfied';
import useThreadsConfiguration from '../shared/hooks/useThreadsConfiguration';

const filterFunc = (items, searchString) => {
  const searchPaths = ['name', 'channel'];
  if (searchString) {
    const search = searchString.toLowerCase().trim();
    return filter(items, item =>
      some(searchPaths, columnKey => get(item, columnKey, '').toLowerCase().includes(search))
    );
  }
  return items;
};

const columnsConfig = [
  {
    attr: 'name',
    label: 'Name',
    customColumnStyle: () => ({ width: '25%' }),
  },
  {
    attr: 'channel',
    label: 'Channel',
    customColumnStyle: () => ({ width: '25%' }),
  },
  {
    attr: 'updatedByString',
    label: 'Last Modified By',
    customColumnStyle: () => ({ width: '25%' }),
  },
  {
    attr: 'updatedAtString',
    label: 'Last Modified On',
    customColumnStyle: () => ({ width: '25%' }),
  },
];

const generateColumnsConfig: any = (isThreadDepSatisfied: boolean) => {
  if (isThreadDepSatisfied) {
    return columnsConfig;
  }
  const configs = columnsConfig.map(config => {
    config.customColumnStyle = () => ({ width: '24%' });
    return config;
  });
  const c: Array<any> = [
    {
      attr: 'warn',
      label: '',
      customRenderer: () => <WarningIcon />,
      customColumnStyle: () => ({ width: '4%' }),
    },
  ];
  configs.forEach(config => c.push(config));
  return c;
};

const renderRowActions = ({ item }) => {
  return <Edit id={item.id} />;
};

const pageDataAid = 'admin-threads-page';

const EmptyPage = ({ root }) => (
  <EmptyStateWrapper data-aid="threads-empty-message">
    <StyledImage src={NewPage} />
    <H3>Create your first Self-Service Thread</H3>

    <Actions root={root} />
  </EmptyStateWrapper>
);

const Actions = ({ root }) => (
  <RouterLinkButton data-aid="action-create-button" to={`${root}/new`}>
    Create Thread
  </RouterLinkButton>
);

const description = (
  <div data-aid="description">
    Create and edit messages for your Self-Service Threads. Learn more about Self-Service in our{' '}
    <ExternalLink
      data-aid="help-link"
      label="Help Docs"
      to="https://help.gladly.com/docs/what-is-self-service-threads"
    />
    .
  </div>
);

const Thread: TablePageRender = ({ root }) => {
  const { values: externalSources = [], loadingState: externalSourcesLoadingState } = useResource(integrationsResource);
  const { items, loadingState, resetErrors, errors } = useThreads();

  const { isFeatureEnabled: isFeatureActivated } = useThreadsConfiguration();

  const [sources, setSources] = useState([]);

  useEffect(() => {
    if (externalSourcesLoadingState === STATUS_SUCCEEDED) {
      setSources(externalSources);
    }
  }, [externalSourcesLoadingState, externalSources]);

  return isLoadingStatus(loadingState) ? (
    <Spinner />
  ) : !isFeatureActivated ? (
    <ActiveThreadsPage />
  ) : (
    <TablePage
      actions={<Actions root={root} />}
      columnConfig={generateColumnsConfig(isThreadDependencySatisfied(sources))}
      data-aid={pageDataAid}
      description={description}
      editor={ThreadsEditor}
      emptyFilterImage={Mailbox}
      emptyStateFullHeightLayout
      emptyStateRender={<EmptyPage root={root} />}
      errors={errors}
      filterFunc={filterFunc}
      isLoading={isLoadingStatus(loadingState)}
      items={items}
      modalTopPadding={16}
      name="Self-Service Threads"
      renderRowActions={renderRowActions}
      resetErrors={resetErrors}
      root={root}
    />
  );
};

const WarningIcon: FC = () => {
  return (
    <StyledWarningFillWrapper>
      <Tooltip data-aid="column-tooltip" message="Shopify, BigCommerce, or a custom Lookup Adaptor must be enabled">
        <StyledWarningFill />
      </Tooltip>
    </StyledWarningFillWrapper>
  );
};

const StyledWarningFillWrapper = styled.div`
  display: flex;
  align-items: center;
  width: 16px;
  height: 16px;

  path {
    fill: ${p => p.theme.colors.yellow400};
  }
`;

const StyledWarningFill = styled(WarningFill)`
  width: 16px;
  height: 16px;
  vertical-align: text-bottom;
`;

const StyledImage = styled.img`
  max-width: 220px;
  margin-bottom: 20px;
`;

export default Thread;
